import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { api } from '@/services/api';

import styles from './ConfigCopiloto.module.css';

interface Parametros {
  gate_first_crawl: number;
  gate_clickbait_risco: number;
  gate_conteudo_original: number;
  gate_largura_capa: number;
  gate_desvio_foco: number;
  gate_goodclicks: number;
  manchete_min: number;
  manchete_max: number;
  manchete_entidade_ate: number;
  lead_palavras_min: number;
  lead_palavras_max: number;
  portao_bloqueia_publicacao: boolean;
}

interface Resposta {
  client: string | null;
  parametros: Parametros;
  padroes: Parametros;
  /** Etapas que consultam um modelo de IA. Hoje: nenhuma. */
  etapas_com_ia: string[];
}

/** campo, rótulo, o que o número decide, unidade. */
const CAMPOS: {
  grupo: string;
  itens: { k: keyof Parametros; label: string; ajuda: string; passo?: number }[];
}[] = [
  {
    grupo: 'Os seis portões',
    itens: [
      { k: 'gate_first_crawl', label: 'Pronto no 1º rastreio', ajuda: 'Nota mínima de 0 a 100. O rastreador prioritário do Google passa uma vez e não volta.' },
      { k: 'gate_clickbait_risco', label: 'Risco de clickbait', ajuda: 'Máximo aceito, de 0 a 1. Acima disso a manchete é reescrita.', passo: 0.01 },
      { k: 'gate_conteudo_original', label: 'Conteúdo original', ajuda: 'Mínimo na escala de 0 a 127. Mede sinais de apuração: tabela, número com unidade, citação, link oficial.' },
      { k: 'gate_largura_capa', label: 'Largura da capa', ajuda: 'Em pixels. O Discover exige 1200 para mostrar imagem grande.' },
      { k: 'gate_desvio_foco', label: 'Desvio do foco do portal', ajuda: 'Máximo de 0 a 1. O Discover avalia autoridade por tópico, não por domínio.', passo: 0.01 },
      { k: 'gate_goodclicks', label: 'Retenção prevista', ajuda: 'Mínimo de 0 a 1. Prevê o clique longo a partir da estrutura do texto.', passo: 0.01 },
    ],
  },
  {
    grupo: 'Régua de manchete',
    itens: [
      { k: 'manchete_min', label: 'Mínimo de caracteres', ajuda: 'Medido sobre o título SERVIDO, com o sufixo que o tema cola.' },
      { k: 'manchete_max', label: 'Máximo de caracteres', ajuda: 'Acima disso o Google trunca e a promessa morre no meio.' },
      { k: 'manchete_entidade_ate', label: 'Entidade até o caractere', ajuda: 'Onde o nome principal precisa aparecer. O olho decide nos primeiros termos.' },
    ],
  },
  {
    grupo: 'Lead',
    itens: [
      { k: 'lead_palavras_min', label: 'Mínimo de palavras', ajuda: 'O primeiro parágrafo é o que o Google usa no trecho em destaque.' },
      { k: 'lead_palavras_max', label: 'Máximo de palavras', ajuda: 'Acima disso o lead deixa de responder e passa a contextualizar.' },
    ],
  },
];

export function ConfigCopiloto() {
  const qc = useQueryClient();
  const [rascunho, setRascunho] = useState<Partial<Parametros>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['copiloto', 'parametros'],
    queryFn: () => api.get<Resposta>('/copilot/parametros'),
    retry: false,
  });

  useEffect(() => setRascunho({}), [q.data]);

  const salvar = useMutation({
    mutationFn: (valores: Partial<Parametros>) =>
      api.put<Resposta>('/copilot/parametros', valores),
    onSuccess: () => {
      setAviso('Salvo. Vale para as próximas análises deste cliente.');
      qc.invalidateQueries({ queryKey: ['copiloto', 'parametros'] });
    },
    onError: (e) => setAviso(`Erro ao salvar: ${String(e)}`),
  });

  const atual = q.data?.parametros;
  const padroes = q.data?.padroes;
  if (!atual || !padroes) {
    return (
      <section className={styles.root}>
        <h1 className={styles.title}>Copiloto — parâmetros</h1>
        <p className={styles.subtitle}>{q.isLoading ? 'Carregando…' : 'Não foi possível ler os parâmetros.'}</p>
      </section>
    );
  }

  const valor = (k: keyof Parametros) => (rascunho[k] ?? atual[k]) as number;
  const mudou = Object.keys(rascunho).length > 0;

  return (
    <section className={styles.root}>
      <h1 className={styles.title}>Copiloto — parâmetros</h1>
      <p className={styles.subtitle}>
        O sistema não roda solto: cada trava do Copiloto tem um número, e o
        número é deste cliente. O que você não alterar vale o padrão da
        plataforma, mostrado ao lado.
      </p>

      {q.data?.etapas_com_ia.length === 0 && (
        <p className={styles.nota}>
          Nenhuma etapa do Copiloto consulta um modelo de IA hoje — os portões
          são medição, a régua de manchete é aritmética e as perguntas do leitor
          saem de modelo de texto. Por isso não há prompt a editar aqui. Quando
          uma etapa passar a usar IA, o prompt dela aparece em{' '}
          <strong>Engenharia &rsaquo; Fluxos</strong>, junto dos que já existem.
        </p>
      )}

      {CAMPOS.map((g) => (
        <div key={g.grupo} className={styles.card}>
          <h2 className={styles.cardTitle}>{g.grupo}</h2>
          {g.itens.map(({ k, label, ajuda, passo }) => {
            const alterado = valor(k) !== padroes[k];
            return (
              <div key={k} className={styles.row}>
                <div className={styles.rowMain}>
                  <label className={styles.label} htmlFor={k}>
                    {label}
                  </label>
                  <input
                    id={k}
                    className={styles.input}
                    type="number"
                    step={passo ?? 1}
                    value={valor(k)}
                    onChange={(e) =>
                      setRascunho((r) => ({ ...r, [k]: Number(e.target.value) }))
                    }
                  />
                  <span className={alterado ? styles.padraoAlterado : styles.padrao}>
                    padrão {String(padroes[k])}
                  </span>
                </div>
                <p className={styles.ajuda}>{ajuda}</p>
              </div>
            );
          })}
        </div>
      ))}

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Comportamento</h2>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={
              (rascunho.portao_bloqueia_publicacao ??
                atual.portao_bloqueia_publicacao) as boolean
            }
            onChange={(e) =>
              setRascunho((r) => ({ ...r, portao_bloqueia_publicacao: e.target.checked }))
            }
          />
          Portão reprovado impede a publicação
        </label>
        <p className={styles.ajuda}>
          Desligado por padrão, e é a decisão certa em notícia factual: o
          rastreador prioritário passa uma vez e não volta, então segurar a
          matéria custa a janela inteira. Reprovação vira pauta, não bloqueio.
        </p>
      </div>

      <div className={styles.acoes}>
        <Button onClick={() => salvar.mutate(rascunho)} disabled={!mudou || salvar.isPending}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
        {mudou && (
          <Button variant="ghost" onClick={() => setRascunho({})}>
            Descartar alterações
          </Button>
        )}
        {aviso && <span className={styles.aviso}>{aviso}</span>}
      </div>
    </section>
  );
}
